import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {MailTemplateSummary} from '../../../../../api';
import {MailTemplateQueryService} from './services/mail-template-query.service';
import {MailTemplateCommandService} from './services/mail-template-command.service';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {ConfirmService} from '../../../../services/confirm.service';
import {ConfirmDialog} from '../../../../components/confirm-dialog/confirm-dialog';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {Search} from '../../../../components/search/search';
import {MailTemplateGrid} from './components/mail-template-grid/mail-template-grid';
import {MailTemplateForm, MailTemplateFormValue} from './components/mail-template-form/mail-template-form';

@Component({
  selector: 'elcia-mail-template-settings',
  imports: [TranslatePipe, ElciaButton, ConfirmDialog, ElciaDialog, MailTemplateGrid, MailTemplateForm, Search],
  providers: [MailTemplateQueryService, MailTemplateCommandService],
  templateUrl: './mail-template-settings.html',
})
export class MailTemplateSettings {
  private readonly queryService = inject(MailTemplateQueryService);
  private readonly commandService = inject(MailTemplateCommandService);
  private readonly confirmService = inject(ConfirmService);

  private readonly mailTemplateForm = viewChild<MailTemplateForm>(MailTemplateForm);

  protected readonly isLoading = computed(() => this.queryService.isLoading());
  protected readonly mailTemplates = computed(() => this.queryService.mailTemplates());
  protected readonly selectedMailTemplate = computed(() => this.queryService.mailTemplateDetails());

  protected readonly isFormDisplayed = signal(false);
  private readonly openMode = signal<'create' | 'edit'>('create');

  protected readonly filterForm = new FormGroup({
    searchTerm: new FormControl('', {nonNullable: true}),
  });

  private readonly filterValue = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.filterForm.value}
  );

  private readonly syncFilter = effect(() => {
    const form = this.filterValue();
    this.queryService.filterParams.set({searchTerm: form.searchTerm || null});
  });

  protected readonly isSaving = computed(() =>
    this.commandService.createMutation.isPending() || this.commandService.updateMutation.isPending()
  );

  protected showForm(): void {
    this.queryService.editingId.set(null);
    this.openMode.set('create');
    this.isFormDisplayed.set(true);
  }

  protected openEditForm(mailTemplate: MailTemplateSummary): void {
    this.queryService.editingId.set(mailTemplate.id);
    this.openMode.set('edit');
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.queryService.editingId.set(null);
  }

  protected onDialogSave(): void {
    this.mailTemplateForm()?.submit();
  }

  protected onFormSubmit(value: MailTemplateFormValue): void {
    if (this.openMode() === 'edit' && value.id != null) {
      this.commandService.updateMutation.mutate({
        id: value.id,
        body: {
          name: value.name!,
          object: value.object!,
          body: value.body!,
          localIsoCode: value.localIsoCode!,
        },
      });
    } else {
      this.commandService.createMutation.mutate({
        name: value.name!,
        object: value.object!,
        body: value.body!,
        localIsoCode: value.localIsoCode!,
      });
    }
  }

  protected deleteMailTemplate(mailTemplate: MailTemplateSummary): void {
    if (!mailTemplate.id) return;
    this.confirmService.confirm({
      title: 'entity.mail-template',
      message: mailTemplate.name,
      accept: () => {
        this.commandService.deleteMutation.mutate(mailTemplate.id!);
      },
    });
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.commandService.createMutation.isSuccess();
    const updateSuccess = this.commandService.updateMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });
}
